import { Link } from "react-router-dom";
import { Quote, Heart, Compass, HandHeart, Sparkles, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { ASSETS } from "@/lib/site";

const VALUES = [
  { icon: Heart, title: "Atendimento humano", text: "Pessoas de verdade cuidando de cada detalhe, com proximidade e empatia." },
  { icon: Compass, title: "Experiência e orientação", text: "Ajudamos você a comparar opções e decidir com mais segurança." },
  { icon: HandHeart, title: "Cuidado em cada etapa", text: "Suporte antes, durante e depois da viagem — você nunca fica sozinho." },
  { icon: Sparkles, title: "Praticidade moderna", text: "Tecnologia para pesquisar com facilidade, sem perder o toque pessoal." },
];

export default function Conheca() {
  return (
    <>
      <PageHero
        overline="Quem somos"
        title="Conheça a Miríade Turismo"
        subtitle="Uma agência de viagens moderna, prática e humana, que ajuda você a descobrir o mundo com segurança e cuidado."
        seoTitle="Conheça a Miríade Turismo"
        seoDescription="Conheça a história, o propósito e o atendimento próximo da Miríade Turismo, sua agência de viagens em São José do Rio Preto."
      />

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <span className="overline">Nossa essência</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Viagens com propósito e atendimento próximo
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-ink-soft">
              <p>
                A Miríade Turismo nasceu do desejo de tornar cada viagem mais simples, segura e
                inesquecível. Acreditamos que viajar é uma das experiências mais transformadoras da
                vida — e que merece ser planejada com carinho, atenção e confiança.
              </p>
              <p>
                Por isso, unimos a praticidade da pesquisa online com o cuidado de um atendimento
                humano. Você pode encontrar sua próxima viagem com poucos cliques e, sempre que
                precisar, contar com uma equipe pronta para orientar a sua escolha.
              </p>
              <p>
                Do primeiro contato ao retorno para casa, nosso compromisso é estar ao seu lado,
                ajudando você a viajar com tranquilidade e a aproveitar cada momento.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CEO editorial */}
      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-10 rounded-3xl border border-border bg-white p-6 shadow-premium sm:p-10 lg:grid-cols-2 lg:gap-16 lg:p-12">
            <Reveal>
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-24 w-24 rounded-3xl bg-brand-light" />
                <div className="absolute -bottom-4 -right-4 h-28 w-28 rounded-3xl bg-sky" />
                <img src={ASSETS.ceo} alt="CEO da Miríade Turismo" className="relative aspect-square w-full max-w-md rounded-3xl object-cover shadow-soft" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <span className="overline">Liderança</span>
                <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
                  Uma equipe que cuida de você
                </h2>
                <p className="mt-5 text-base leading-relaxed text-ink-soft">
                  À frente da Miríade Turismo está uma liderança apaixonada por viagens e por pessoas.
                  Essa proximidade se reflete em cada atendimento: ouvir com atenção, entender o que
                  você procura e orientar com honestidade e cuidado.
                </p>
                <div className="mt-7 rounded-2xl border-l-4 border-brand bg-brand-light/50 p-5">
                  <Quote size={22} className="text-brand" />
                  <p className="mt-2 font-heading text-lg font-medium italic text-ink">
                    “Viajar é uma decisão importante. Nosso compromisso é ajudar você a escolher com
                    tranquilidade, segurança e cuidado.”
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle center overline="No que acreditamos" title="Nossos valores" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-border bg-white p-8 shadow-soft transition-all hover:-translate-y-1 hover:shadow-hover">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light">
                    <v.icon size={26} className="text-brand" />
                  </div>
                  <h3 className="mt-6 font-heading text-lg font-semibold text-ink">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-soft">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/buscar-viagens" className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-all hover:scale-[1.02] hover:bg-brand-hover">
              Buscar minha viagem <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <CTASection title="Vamos planejar sua próxima viagem juntos?" />
    </>
  );
}
